import { useEffect, useState } from 'react';
import type { InstanceInvite, SelfUser } from '@comms/shared';
import { api } from '../lib/api';
import { Button, ErrorText, Input, Tabs } from '../components/ui';
import { NavButton } from '../components/MobileNav';
import { HealthOverview } from '../components/HealthOverview';

type AdminUser = SelfUser & { disabled: boolean };

export function AdminPanel() {
  const [invites, setInvites] = useState<InstanceInvite[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [tab, setTab] = useState<'overview' | 'users' | 'invites'>('overview');
  const [maxUses, setMaxUses] = useState(1);
  const [hours, setHours] = useState(72);
  const [note, setNote] = useState('');
  const [error, setError] = useState<unknown>(null);
  const load = () => {
    void api<InstanceInvite[]>('GET', '/admin/invites').then(setInvites);
    void api<AdminUser[]>('GET', '/admin/users').then(setUsers);
  };
  useEffect(load, []);

  async function createInvite() {
    try {
      await api('POST', '/admin/invites', {
        maxUses,
        expiresInHours: hours,
        ...(note ? { note } : {}),
      });
      setNote('');
      load();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-8">
      <div className="max-w-4xl space-y-6">
        <div className="flex items-center gap-2">
          <NavButton />
          <h1 className="text-2xl font-bold text-text-0">Instance administration</h1>
        </div>
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { id: 'overview', label: 'Overview' },
            { id: 'users', label: `Users (${users.length})` },
            { id: 'invites', label: 'Invites' },
          ]}
        />
        {tab === 'overview' && <HealthOverview />}
        {tab === 'invites' && (
          <section>
            <h2 className="mb-3 text-lg font-semibold text-text-0">Registration invites</h2>
            <div className="mb-4 flex flex-wrap items-end gap-2">
              <Input
                label="Uses"
                type="number"
                min={1}
                max={100}
                value={maxUses}
                onChange={(e) => setMaxUses(Number(e.target.value))}
                className="w-24"
              />
              <Input
                label="Expires in (hours)"
                type="number"
                min={1}
                value={hours}
                onChange={(e) => setHours(Number(e.target.value))}
                className="w-36"
              />
              <Input
                label="Note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="for Sam"
              />
              <Button onClick={createInvite}>Create invite</Button>
            </div>
            <ErrorText error={error} />
            <div className="divide-y divide-bg-3">
              {invites.map((i) => {
                const link = `${location.origin}/register?invite=${i.code}`;
                return (
                  <div key={i.code} className="flex items-center gap-3 py-2 text-sm">
                    <code className="text-text-0">{i.code}</code>
                    <span className="text-text-3">{i.note}</span>
                    <span className="ml-auto text-text-3">
                      {i.uses}/{i.maxUses} used ·{' '}
                      {i.expiresAt
                        ? `expires ${new Date(i.expiresAt).toLocaleDateString()}`
                        : 'no expiry'}
                    </span>
                    <Button
                      variant="secondary"
                      className="py-1"
                      onClick={() => void navigator.clipboard.writeText(link)}
                    >
                      Copy link
                    </Button>
                    <Button
                      variant="ghost"
                      className="py-1 text-danger"
                      onClick={() => void api('DELETE', `/admin/invites/${i.code}`).then(load)}
                    >
                      Delete
                    </Button>
                  </div>
                );
              })}
            </div>
          </section>
        )}
        {tab === 'users' && (
          <section>
            <h2 className="mb-3 text-lg font-semibold text-text-0">Users</h2>
            <div className="divide-y divide-bg-3">
              {users.map((u) => (
                <div key={u.id} className="flex items-center gap-3 py-2 text-sm">
                  <span className="text-text-0">{u.displayName}</span>
                  <span className="text-text-3">{u.username}</span>
                  {u.isAdmin && (
                    <span className="rounded bg-warning/20 px-1 text-xs text-warning">admin</span>
                  )}
                  {u.disabled && (
                    <span className="rounded bg-danger/20 px-1 text-xs text-danger">disabled</span>
                  )}
                  <span className="ml-auto text-xs text-text-3">
                    joined {new Date(u.createdAt).toLocaleDateString()}
                  </span>
                  <Button
                    variant="ghost"
                    className="py-1"
                    onClick={() =>
                      void api('PATCH', `/admin/users/${u.id}`, { isAdmin: !u.isAdmin }).then(
                        load,
                        setError,
                      )
                    }
                  >
                    {u.isAdmin ? 'Remove admin' : 'Make admin'}
                  </Button>
                  <Button
                    variant={u.disabled ? 'secondary' : 'danger'}
                    className="py-1"
                    onClick={() =>
                      (u.disabled ||
                        confirm(`Disable ${u.username}? They'll be signed out everywhere.`)) &&
                      api('PATCH', `/admin/users/${u.id}`, { disabled: !u.disabled }).then(
                        load,
                        setError,
                      )
                    }
                  >
                    {u.disabled ? 'Enable' : 'Disable'}
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
